import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, X, Search, User, Bookmark, Calendar, 
  ChevronDown, Newspaper, Sun, Moon, LogOut, BookOpen
} from 'lucide-react';
import { useNewspaper, useAuth } from '../../context/AppContext';
import { useScrollPosition } from '../../hooks';
import SearchModal from './SearchModal';
import RegionSelector from './RegionSelector';
import ThemeSwitcher from './ThemeSwitcher';
import styles from './Header.module.css';

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { config, categories, latestEdition } = useNewspaper();
  const { isAuthenticated, user, canAccessAdmin, logout } = useAuth();

  const handleQuickLogout = () => {
    logout();
    // Recarga suave a la home; asegura que cualquier estado en memoria
    // que dependa de la sesión se limpie.
    window.location.href = '/';
  };
  const { scrollPosition, isScrollingUp } = useScrollPosition();
  const navigate = useNavigate();
  const location = useLocation();

  const isScrolled = scrollPosition > 100;
  const showHeader = isScrollingUp || scrollPosition < 100;

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  const formatDate = () => {
    const date = new Date();
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const mainCategories = categories.slice(0, 7);
  const moreCategories = categories.slice(7);

  return (
    <>
      <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''} ${showHeader ? '' : styles.hidden}`}>
        {/* Barra superior */}
        <div className={styles.topBar}>
          <div className={`container ${styles.topBarContent}`}>
            <div className={styles.topBarLeft}>
              <Calendar size={14} />
              <span className={styles.date}>{formatDate()}</span>
              {latestEdition && (
                <span className={styles.editionNumber}>
                  Edición N° {latestEdition.number}
                </span>
              )}
            </div>
            <div className={styles.topBarRight}>
              <RegionSelector />
              {isAuthenticated ? (
                canAccessAdmin ? (
                  <Link to="/admin" className={styles.topBarLink}>
                    <User size={14} />
                    <span>{user?.name}</span>
                  </Link>
                ) : (
                  // Usuario autenticado pero sin rol interno de News: no
                  // hay link al admin, pero SÍ mostramos botón para cerrar
                  // sesión — si no, queda pegado sin poder salir.
                  <>
                    <span className={styles.topBarLink} style={{ opacity: 0.85, cursor: 'default' }}>
                      <User size={14} />
                      <span>{user?.name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={handleQuickLogout}
                      className={styles.topBarLink}
                      style={{
                        background: 'none', border: 'none',
                        cursor: 'pointer', padding: 0,
                        color: 'inherit', font: 'inherit',
                      }}
                      title="Cerrar sesión"
                    >
                      <LogOut size={14} />
                      <span>Salir</span>
                    </button>
                  </>
                )
              ) : (
                <Link to="/login" className={styles.topBarLink}>
                  <User size={14} />
                  <span>Iniciar sesión</span>
                </Link>
              )}
              <Link to="/ediciones" className={styles.topBarLink}>
                <Newspaper size={14} />
                <span>Ediciones</span>
              </Link>
              {latestEdition && (
                <Link
                  to={`/edicion/${latestEdition.id}/revista`}
                  className={styles.topBarLink}
                  title="Ver última edición en modo revista"
                >
                  <BookOpen size={14} />
                  <span>Revista</span>
                </Link>
              )}
              <Link to="/guardados" className={styles.topBarLink}>
                <Bookmark size={14} />
                <span>Guardados</span>
              </Link>
              <ThemeSwitcher variant="inline" />
            </div>
          </div>
        </div>

        {/* Cabecera principal */}
        <div className={styles.mainHeader}>
          <div className={`container ${styles.mainHeaderContent}`}>
            <button 
              className={styles.menuButton}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <Link to="/" className={styles.logo}>
              <div className={styles.logoText}>
                <span className={styles.logoMain}>{config?.name || ''}</span>
                <span className={styles.logoSlogan}>{config?.description || ''}</span>
              </div>
            </Link>

            <button 
              className={styles.searchButton}
              onClick={() => setSearchOpen(true)}
              aria-label="Buscar"
            >
              <Search size={22} />
            </button>
          </div>
        </div>

        {/* Navegación */}
        <nav className={styles.nav}>
          <div className={`container ${styles.navContent}`}>
            <ul className={styles.navList}>
              {mainCategories.map(category => {
                const [head, ...tailWords] = category.name.split(' ');
                const tail = tailWords.join(' ');
                return (
                  <li key={category.id} className={styles.navItem}>
                    <Link
                      to={`/seccion/${category.slug}`}
                      className={`${styles.navLink} ${location.pathname.includes(category.slug) ? styles.active : ''}`}
                      title={category.name}
                    >
                      <span className={styles.navLabel}>
                        <span className={styles.navHead}>{head}</span>
                        {tail && <span className={styles.navTail}>&nbsp;{tail}</span>}
                      </span>
                    </Link>
                  </li>
                );
              })}
              {moreCategories.length > 0 && (
                <li className={styles.navItem}>
                  <button 
                    className={styles.navLink}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveDropdown(activeDropdown === 'more' ? null : 'more');
                    }}
                  >
                    Más <ChevronDown size={16} />
                  </button>
                  {activeDropdown === 'more' && (
                    <ul className={styles.dropdown}>
                      {moreCategories.map(category => (
                        <li key={category.id}>
                          <Link to={`/seccion/${category.slug}`} className={styles.dropdownLink}>
                            {category.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
            </ul>
          </div>
        </nav>

        {/* Menú móvil */}
        <div className={`${styles.mobileMenu} ${menuOpen ? styles.mobileMenuOpen : ''}`}>
          <div className={styles.mobileMenuContent}>
            <div className={styles.mobileMenuHeader}>
              <span className={styles.mobileMenuTitle}>Secciones</span>
              <button onClick={() => setMenuOpen(false)}>
                <X size={24} />
              </button>
            </div>
            <ul className={styles.mobileNavList}>
              {categories.map(category => (
                <li key={category.id}>
                  <Link 
                    to={`/seccion/${category.slug}`}
                    className={styles.mobileNavLink}
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className={styles.mobileMenuFooter}>
              <Link to="/ediciones" className={styles.mobileMenuLink}>
                <Newspaper size={18} />
                Ver ediciones impresas
              </Link>
              {isAuthenticated && canAccessAdmin && (
                <Link to="/admin" className={styles.mobileMenuLink}>
                  <User size={18} />
                  Panel de administración
                </Link>
              )}
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={handleQuickLogout}
                  className={styles.mobileMenuLink}
                  style={{
                    background: 'none', border: 'none',
                    cursor: 'pointer', textAlign: 'left',
                    font: 'inherit', color: 'inherit', width: '100%',
                  }}
                >
                  <LogOut size={18} />
                  Cerrar sesión
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Modal de búsqueda */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Spacer */}
      <div className={styles.headerSpacer} />
    </>
  );
}
